import { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { trafficSignalAPI, BASE_URL } from '../services/api';
import './Brands.css';

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  return `${BASE_URL}${imagePath}`;
};

function TrafficSignals() {
  const [signals, setSignals] = useState([]);
  const [locales, setLocales] = useState([]);
  const [availableLocales, setAvailableLocales] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResults, setSyncResults] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 12);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [localeFilter, setLocaleFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Create/Edit modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingSignal, setEditingSignal] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    locale: '',
    category: '',
    name: '',
    description: '',
    image: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch locales with data
  const fetchLocales = async () => {
    try {
      const response = await trafficSignalAPI.getLocales();
      setLocales(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locales:', err);
    }
  };

  // Fetch available locales for syncing
  const fetchAvailableLocales = async () => {
    try {
      const response = await trafficSignalAPI.getAvailableLocales();
      setAvailableLocales(response.data || []);
    } catch (err) {
      console.error('Failed to fetch available locales:', err);
    }
  };

  // Fetch categories
  const fetchCategories = async (locale) => {
    try {
      const params = locale ? { locale } : {};
      const response = await trafficSignalAPI.getCategories(params);
      setCategories(response.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchSignals = async (currentPage, currentLimit, currentSearch, currentLocale, currentCategory) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentLocale) params.locale = currentLocale;
      if (currentCategory) params.category = currentCategory;

      const response = await trafficSignalAPI.getAll(params);

      if (Array.isArray(response.data)) {
        setSignals(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setSignals(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch traffic signals');
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocales();
    fetchAvailableLocales();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchCategories(localeFilter);
  }, [localeFilter]);

  useEffect(() => {
    fetchSignals(page, limit, search, localeFilter, categoryFilter);
  }, [page, limit, search, localeFilter, categoryFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this traffic signal?')) {
      try {
        await trafficSignalAPI.delete(id);
        fetchSignals(page, limit, search, localeFilter, categoryFilter);
      } catch (err) {
        setError('Failed to delete traffic signal');
      }
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingSignal(null);
    setFormData({
      locale: localeFilter || 'en',
      category: '',
      name: '',
      description: '',
      image: ''
    });
    setImagePreview(null);
    setShowFormModal(true);
  };

  // Open edit modal
  const handleEdit = (signal) => {
    setEditingSignal(signal);
    setFormData({
      locale: signal.locale || '',
      category: signal.category || '',
      name: signal.name || '',
      description: signal.description || '',
      image: signal.image || ''
    });
    setImagePreview(signal.image ? getImageUrl(signal.image) : null);
    setShowFormModal(true);
  };

  // Handle form input change
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit form (create or update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      if (editingSignal) {
        await trafficSignalAPI.update(editingSignal.id, formData);
      } else {
        await trafficSignalAPI.create(formData);
      }
      setShowFormModal(false);
      fetchSignals(page, limit, search, localeFilter, categoryFilter);
      fetchLocales();
      fetchCategories(localeFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save traffic signal');
    } finally {
      setFormLoading(false);
    }
  };

  // Close form modal
  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingSignal(null);
    setFormData({ locale: '', category: '', name: '', description: '', image: '' });
    setImagePreview(null);
  };

  // Sync signals for selected locale
  const handleSyncSelected = async () => {
    if (!localeFilter) {
      setError('Please select a language first');
      return;
    }

    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    try {
      const response = await trafficSignalAPI.sync(localeFilter);
      const data = response.data;

      if (data.success) {
        setSyncResults([{
          locale: data.locale,
          languageName: data.languageName,
          count: data.totalSynced
        }]);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync traffic signals');
    }

    setSyncComplete(true);
    setSyncing(false);
    fetchLocales();
    fetchSignals(page, limit, search, localeFilter, categoryFilter);
  };

  // Sync all traffic signals for all languages
  const handleSyncAll = async () => {
    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    try {
      const response = await trafficSignalAPI.syncAll();
      const data = response.data;

      if (data.success) {
        setSyncResults(data.results || []);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync traffic signals');
    }

    setSyncComplete(true);
    setSyncing(false);
    fetchLocales();
    fetchSignals(page, limit, search, localeFilter, categoryFilter);
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleLocaleFilterChange = (e) => {
    setLocaleFilter(e.target.value);
    setCategoryFilter('');
    setPage(1);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
    setPage(1);
  };

  const getTotalSynced = () => {
    return syncResults.reduce((sum, r) => sum + (r.count || 0), 0);
  };

  const getLanguageName = (locale) => {
    const found = availableLocales.find(l => l.locale === locale) || locales.find(l => l.locale === locale);
    return found ? found.languageName : locale;
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Progress Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{syncComplete ? 'Sync Complete' : 'Syncing Traffic Signals...'}</h2>
              {syncComplete && <button className="modal-close" onClick={closeSyncModal}>&times;</button>}
            </div>
            <div className="modal-body sync-modal-body">
              {syncing && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p className="sync-status">
                    <strong>Fetching traffic signals from API...</strong>
                  </p>
                  <p className="sync-counts">
                    This may take a few minutes for all languages
                  </p>
                  <div className="progress-bar">
                    <div className="progress-fill indeterminate" style={{ width: '30%' }}></div>
                  </div>
                  <p className="progress-percent">Please wait...</p>
                </div>
              )}

              {syncComplete && syncResults.length > 0 && (
                <div className="sync-results">
                  <h3>Total: {getTotalSynced()} traffic signals synced</h3>
                  <div className="results-grid">
                    {syncResults.filter(r => r.count > 0).map((result, index) => (
                      <div key={index} className="result-item success">
                        <span className="state-name">{result.languageName || result.locale}</span>
                        <span className="count">{result.count} signals</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {syncComplete && syncResults.length === 0 && (
                <div className="sync-results">
                  <h3>No new data synced</h3>
                  <p>All traffic signals are already up to date.</p>
                </div>
              )}
            </div>
            {syncComplete && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={closeSyncModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSignal ? 'Edit Traffic Signal' : 'Add Traffic Signal'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Language *</label>
                  <select
                    name="locale"
                    value={formData.locale}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">Select Language</option>
                    {availableLocales.map(l => (
                      <option key={l.locale} value={l.locale}>{l.languageName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    placeholder="e.g., Warning Signs, Regulatory Signs"
                  />
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Signal name"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Description of the traffic signal"
                  />
                </div>
                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingSignal ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Traffic Signals</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Signal
          </button>
          <div className="sync-dropdown">
            <button
              className="btn btn-sync"
              onClick={() => setShowSyncOptions(!showSyncOptions)}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Data'} ▼
            </button>
            {showSyncOptions && (
              <div className="sync-options-menu">
                {localeFilter && (
                  <button onClick={handleSyncSelected} className="sync-option">
                    <strong>Sync Selected Language</strong>
                    <span>Sync signals for {getLanguageName(localeFilter)}</span>
                  </button>
                )}
                <button onClick={handleSyncAll} className="sync-option">
                  <strong>Sync All Languages</strong>
                  <span>Sync traffic signals for all languages</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search signals..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select value={localeFilter} onChange={handleLocaleFilterChange} className="filter-select">
              <option value="">All Languages</option>
              {locales.map(locale => (
                <option key={locale.locale} value={locale.locale}>
                  {locale.languageName}
                </option>
              ))}
            </select>
            <select value={categoryFilter} onChange={handleCategoryFilterChange} className="filter-select">
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="6">6</option>
                <option value="12">12</option>
                <option value="24">24</option>
                <option value="48">48</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : signals.length === 0 ? (
          <div className="empty-state">
            <p>No traffic signals found. {search || localeFilter || categoryFilter ? 'Try adjusting your filters.' : 'Please sync traffic signals first!'}</p>
          </div>
        ) : (
          <>
            <div className="signals-grid">
              {signals.map((signal) => (
                <div key={signal.id} className="signal-card">
                  <div className="signal-image">
                    {signal.image ? (
                      <img src={getImageUrl(signal.image)} alt={signal.name} />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </div>
                  <div className="signal-content">
                    <h3 className="signal-name">{signal.name}</h3>
                    {signal.category && (
                      <span className="signal-category">{signal.category}</span>
                    )}
                    {signal.description && (
                      <p className="signal-description">{signal.description}</p>
                    )}
                    <div className="signal-meta">
                      <span className="signal-locale">{getLanguageName(signal.locale)}</span>
                    </div>
                  </div>
                  <div className="signal-actions">
                    {signal.image && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                          navigator.clipboard.writeText(getImageUrl(signal.image));
                          setCopiedId(signal.id);
                          setTimeout(() => setCopiedId(null), 2000);
                        }}
                      >
                        {copiedId === signal.id ? 'Copied!' : 'Copy URL'}
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(signal)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(signal.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .sync-dropdown {
          position: relative;
          display: inline-block;
        }
        .sync-options-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 280px;
          z-index: 1000;
          margin-top: 5px;
        }
        .sync-option {
          display: block;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          border-bottom: 1px solid #eee;
        }
        .sync-option:last-child {
          border-bottom: none;
        }
        .sync-option:hover {
          background-color: #f5f5f5;
        }
        .sync-option strong {
          display: block;
          color: #333;
          margin-bottom: 2px;
        }
        .sync-option span {
          display: block;
          font-size: 12px;
          color: #666;
        }
        .sync-progress {
          text-align: center;
          padding: 20px;
        }
        .sync-status {
          margin: 15px 0 5px;
          font-size: 16px;
        }
        .sync-counts {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
        }
        .progress-percent {
          margin-top: 8px;
          font-weight: bold;
          color: #007bff;
        }
        .sync-results {
          margin-top: 10px;
        }
        .sync-results h3 {
          margin-bottom: 15px;
          color: #28a745;
          text-align: center;
        }
        .sync-results p {
          text-align: center;
          color: #666;
        }
        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 10px;
          max-height: 300px;
          overflow-y: auto;
        }
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          border-radius: 4px;
          font-size: 14px;
        }
        .result-item.success {
          background-color: #d4edda;
          color: #155724;
        }
        .result-item .state-name {
          font-weight: 500;
        }
        .result-item .count {
          font-size: 12px;
          opacity: 0.8;
        }
        .progress-bar {
          width: 100%;
          height: 10px;
          background-color: #e9ecef;
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }
        .progress-fill.indeterminate {
          animation: indeterminate 1.5s infinite ease-in-out;
        }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
        .signals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
          padding: 20px 0;
        }
        .signal-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }
        .signal-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          transform: translateY(-2px);
        }
        .signal-image {
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
          padding: 20px;
          position: relative;
        }
        .signal-image img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        .no-image {
          color: #aaa;
          font-size: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .no-image::before {
          /* Line icon rather than the emoji this used to render, so it matches
             the rest of the admin on every platform. */
          content: '';
          width: 32px;
          height: 32px;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23aaaaaa' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 21a9 9 0 100-18 9 9 0 000 18zM5.6 5.6l12.8 12.8'/%3E%3C/svg%3E") no-repeat center / contain;
          opacity: 0.5;
        }
        .signal-content {
          padding: 16px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .signal-name {
          font-size: 17px;
          font-weight: 600;
          margin: 0 0 10px 0;
          color: #1a1a2e;
          line-height: 1.3;
        }
        .signal-category {
          display: inline-block;
          padding: 4px 10px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 10px;
          letter-spacing: 0.5px;
          width: fit-content;
        }
        .signal-description {
          font-size: 13px;
          color: #555;
          line-height: 1.5;
          margin: 8px 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1;
        }
        .signal-meta {
          margin-top: auto;
          padding-top: 12px;
          border-top: 1px solid #eee;
        }
        .signal-locale {
          font-size: 12px;
          color: #888;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #f0f0f0;
          padding: 4px 8px;
          border-radius: 4px;
        }
        .signal-locale::before {
          content: '';
          width: 12px;
          height: 12px;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 21a9 9 0 100-18 9 9 0 000 18zM3 12h18M12 3a14 14 0 000 18 14 14 0 000-18z'/%3E%3C/svg%3E") no-repeat center / contain;
        }
        .signal-actions {
          padding: 12px 16px;
          border-top: 1px solid #eee;
          background: #fafafa;
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .signal-actions .btn {
          padding: 6px 14px;
          font-size: 13px;
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .signal-actions .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .signal-actions .btn-secondary:hover {
          background: #e0e0e0;
        }
        .signal-actions .btn-danger {
          background: #fff0f0;
          color: #dc3545;
          border: 1px solid #ffcdd2;
        }
        .signal-actions .btn-danger:hover {
          background: #dc3545;
          color: white;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }
        .modal {
          background: white;
          border-radius: 16px;
          max-width: 520px;
          width: 95%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: modalSlideIn 0.3s ease;
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          position: relative;
        }
        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          text-align: left;
        }
        .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
        }
        .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .modal-body {
          padding: 24px;
          max-height: calc(90vh - 160px);
          overflow-y: auto;
        }
        .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          background: #fafafa;
        }

        /* Form Styles */
        .form-group {
          margin-bottom: 20px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 14px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s ease;
          background: #fafafa;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }
        .form-group input[type="file"] {
          padding: 10px;
          cursor: pointer;
        }
        .image-preview {
          margin-top: 12px;
          padding: 16px;
          border: 2px dashed #ddd;
          border-radius: 8px;
          background: #fafafa;
          text-align: center;
        }
        .image-preview img {
          max-width: 200px;
          max-height: 150px;
          object-fit: contain;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        /* Button Styles */
        .btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }
        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }
        .btn-primary:hover {
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          transform: translateY(-1px);
        }
        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }
        .btn-secondary {
          background: #f0f0f0;
          color: #333;
          border: 1px solid #ddd;
        }
        .btn-secondary:hover {
          background: #e0e0e0;
        }
        .btn-sync {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(17, 153, 142, 0.3);
        }
        .btn-sync:hover {
          box-shadow: 0 4px 12px rgba(17, 153, 142, 0.4);
        }
        .btn-sync:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Sync Modal Styles */
        .sync-modal {
          background: white;
          border-radius: 16px;
          max-width: 600px;
          width: 95%;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }
        .sync-modal .modal-header {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        }
        .sync-modal-body {
          padding: 24px;
        }
      `}</style>
    </div>
  );
}

export default TrafficSignals;
